import { useCallback, useRef, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

/**
 * Tracks which catalog entity refs actually exist, so the UI can avoid
 * rendering links that would 404. Pages collect candidate refs (built via
 * `toComponentRef` / `toGroupRef`) and feed them to `addRefs`; the tracker
 * batches uncached refs into a single `catalogApi.getEntitiesByRefs` call
 * and exposes a `Set<string>` of refs known to exist.
 *
 * Results are memoised in a module-level cache for the session — navigating
 * between security-insights pages reuses prior lookups instead of re-asking
 * the catalog.
 *
 * On API failure, refs stay un-cached and un-existing → callers fall back
 * to rendering plain text, which is the safe default.
 */

// Session-level cache shared across hook instances. Keyed by entity ref.
// `true` = entity exists; `false` = catalog responded but it was missing.
// Absent = not yet checked.
const sessionCache = new Map<string, boolean>();

export interface EntityExistenceTracker {
  /** Refs known to exist in the catalog. */
  existing: Set<string>;
  /**
   * Submit candidate refs for existence checking. Safe to call from effects
   * after data loads; duplicate / already-checked refs are skipped, so
   * spamming the same refs is cheap.
   */
  addRefs: (refs: ReadonlyArray<string | null | undefined>) => void;
}

export function useEntityExistenceTracker(): EntityExistenceTracker {
  const catalogApi = useApi(catalogApiRef);

  const [existing, setExisting] = useState<Set<string>>(() => {
    const seeded = new Set<string>();
    sessionCache.forEach((value, ref) => {
      if (value) seeded.add(ref);
    });
    return seeded;
  });

  // Track in-flight refs so concurrent `addRefs` calls don't issue duplicate
  // requests for the same ref.
  const inFlightRef = useRef<Set<string>>(new Set());

  const addRefs = useCallback(
    (refs: ReadonlyArray<string | null | undefined>) => {
      const toQuery: string[] = [];
      const seen = new Set<string>();
      for (const ref of refs) {
        if (!ref || seen.has(ref)) continue;
        seen.add(ref);
        if (sessionCache.has(ref) || inFlightRef.current.has(ref)) continue;
        toQuery.push(ref);
      }
      if (toQuery.length === 0) return;

      toQuery.forEach(r => inFlightRef.current.add(r));

      catalogApi
        .getEntitiesByRefs({
          entityRefs: toQuery,
          fields: ['kind', 'metadata.name', 'metadata.namespace'],
        })
        .then(({ items }) => {
          const newlyFound: string[] = [];
          items.forEach((entity, i) => {
            const ref = toQuery[i];
            const found = Boolean(entity);
            sessionCache.set(ref, found);
            if (found) newlyFound.push(ref);
          });
          if (newlyFound.length > 0) {
            setExisting(prev => {
              const next = new Set(prev);
              newlyFound.forEach(r => next.add(r));
              return next;
            });
          }
        })
        .catch(() => {
          // Swallow — refs stay un-cached, callers render plain text.
        })
        .finally(() => {
          toQuery.forEach(r => inFlightRef.current.delete(r));
        });
    },
    [catalogApi],
  );

  return { existing, addRefs };
}
